// DPS (monthly deposit scheme) and FDR (fixed deposit) — Personal Savings instruments (v10).
// The account's balance is what has really been paid in (deposits arrive as transfers); the terms below live in
// money_accounts.details and drive the schedule, the progress and an ESTIMATE of the maturity value (banks round and tax
// differently, so the app always labels it "approximate").
import { z } from 'zod';
import { D, Decimal } from './money';
import { zDate, zMoneyPos, zText } from './sync/primitives';
import { addMonthsToDate as addMonths } from './time';

export const zSavingsDetails = z.object({
  institution: zText(80).nullable().optional(),
  accountNo: zText(40).nullable().optional(),
  /** DPS: the monthly instalment */
  installment: zMoneyPos.nullable().optional(),
  /** DPS: day of the month the instalment is due (1–28) */
  dueDay: z.number().int().min(1).max(28).nullable().optional(),
  /** FDR: the amount fixed */
  principal: zMoneyPos.nullable().optional(),
  /** yearly profit / interest rate in % */
  ratePct: z.string().regex(/^\d{1,2}(\.\d{1,2})?$/).nullable().optional(),
  tenureMonths: z.number().int().min(1).max(360).nullable().optional(),
  startDate: zDate.nullable().optional(),
  maturityDate: zDate.nullable().optional(),
  autoRenew: z.boolean().optional(),
  status: z.enum(['running', 'matured', 'closed']).optional(),
});
export type SavingsDetails = z.infer<typeof zSavingsDetails>;

export const maturityDateOf = (s: Pick<SavingsDetails, 'startDate' | 'tenureMonths' | 'maturityDate'>): string | null =>
  s.maturityDate ?? (s.startDate && s.tenureMonths ? addMonths(s.startDate, s.tenureMonths) : null);

/** Whole months from `from` to `to` (both YYYY-MM-DD), never negative. */
export function monthsBetween(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  return Math.max(0, (ty - fy) * 12 + (tm - fm) - (td < fd ? 1 : 0));
}

/**
 * DPS estimate: each instalment earns monthly-compounded profit until maturity — FV = P · ((1+r)^n − 1) / r · (1+r), r = rate/12.
 * FDR estimate: profit compounded quarterly (the common practice in Bangladesh) over the tenure.
 */
export function estimatedMaturity(type: 'dps' | 'fdr', s: SavingsDetails): Decimal | null {
  const n = s.tenureMonths ?? 0;
  const rate = s.ratePct ? D(s.ratePct).div(100) : null;
  if (!n || rate === null) return null;
  if (type === 'dps') {
    if (!s.installment) return null;
    const r = rate.div(12);
    if (r.isZero()) return D(s.installment).times(n);
    const g = r.plus(1).pow(n);
    return D(s.installment).times(g.minus(1).div(r)).times(r.plus(1)).toDecimalPlaces(2);
  }
  if (!s.principal) return null;
  const q = rate.div(4);
  return D(s.principal).times(q.plus(1).pow(n / 3)).toDecimalPlaces(2);
}

export interface DpsSchedule { due: number; paid: number; behind: number; nextDue: string | null; total: number }
/** How many instalments should have been paid by `today`, how many the balance covers, and the next due date. */
export function dpsSchedule(s: SavingsDetails, balance: Decimal.Value, today: string): DpsSchedule | null {
  if (!s.startDate || !s.installment || D(s.installment).lte(0)) return null;
  const total = s.tenureMonths ?? 0;
  const day = s.dueDay ?? Number(s.startDate.slice(8, 10));
  const firstDue = `${s.startDate.slice(0, 8)}${String(Math.min(day, 28)).padStart(2, '0')}`;
  let due = today < firstDue ? 0 : monthsBetween(firstDue, today) + 1;
  if (total) due = Math.min(due, total);
  const paid = Math.floor(D(balance).div(s.installment).toNumber());
  const nextIdx = Math.max(paid, 0);
  const nextDue = total && nextIdx >= total ? null : addMonths(firstDue, nextIdx);
  return { due, paid, behind: Math.max(0, due - paid), nextDue, total };
}
